// Campaign Projects — active Company/Project context (task 11.1).
//
// Holds the tenant's companies (each with its active projects), the flat active-project
// list, and the currently active project/company. The active project id is mirrored into
// api.js (setActiveProjectId) so every backend call carries 'X-Project-Id' from one place.
// Selection is persisted per-user in localStorage and falls back to the Default Project
// whenever the stored one is missing/archived (R24.2).
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  listCompanies, listProjects, createProject as apiCreateProject,
  createCompany as apiCreateCompany, renameProject as apiRenameProject,
  renameCompany as apiRenameCompany, archiveProject as apiArchiveProject,
  reactivateProject as apiReactivateProject, deleteProject as apiDeleteProject,
  setActiveProjectId,
} from "./api";
import { useUploadQueue } from "./UploadQueueContext";

const DEFAULT_PROJECT_ID = "default";
const DEFAULT_COMPANY_ID = "default_company";

const ProjectContext = createContext(null);

export function useProjects() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProjects must be used within <ProjectProvider>");
  return ctx;
}

function storageKey(userKey) {
  return `boomerang.activeProject.${userKey || "default"}`;
}

export function ProjectProvider({ userKey, enabled = true, children }) {
  const [companies, setCompanies] = useState([]);      // [{companyId, displayName, projects:[...]}]
  const [projects, setProjects] = useState([]);        // flat active projects
  const [activeProjectId, setActiveProjectIdState] = useState(DEFAULT_PROJECT_ID);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const didInit = useRef(false);

  // Keep api.js in sync so the X-Project-Id header is attached to every call.
  useEffect(() => { setActiveProjectId(activeProjectId); }, [activeProjectId]);

  const activeProject = projects.find((p) => p.projectId === activeProjectId) || null;
  const activeCompanyId = activeProject?.companyId || DEFAULT_COMPANY_ID;
  const activeCompany = companies.find((c) => c.companyId === activeCompanyId) || null;

  const persist = useCallback((pid) => {
    try { localStorage.setItem(storageKey(userKey), pid); } catch (e) { /* ignore */ }
  }, [userKey]);

  // An upload batch is BOUND to the project it started in (see UploadQueueContext /
  // uploadProjectScope). Switching mid-batch was silently scattering one upload's files across
  // projects and, for a large file, hard-failing `/upload/ingest` with 403 "Access denied." when
  // the presigned record and the ingest call disagreed. Every file is now pinned, so this is the
  // second, PREVENTIVE layer: don't let the situation arise at all.
  // NOTE this is a UI-level guard only — `refresh()` below re-resolves the active project directly
  // (e.g. the stored one was archived), so it can still change without passing through here. That
  // is exactly why the per-file pinning, not this, is the correctness guarantee.
  const { batchQueue, batchDone } = useUploadQueue();
  const projectSwitchBlocked = !!batchQueue && !batchDone;

  // `force` exists for a REAL reason, not convenience: archive()/remove() below switch away from
  // the project being deleted. Blocking those would leave the app pointed at a project that no
  // longer exists — strictly worse than the bug this guard prevents. Only USER-INITIATED switches
  // (the ProjectBar dropdown, "Switch to it" after creating a project) are gated.
  // Returns true if the switch happened, false if it was refused, so a caller can react.
  const selectProject = useCallback((pid, { force = false } = {}) => {
    if (projectSwitchBlocked && !force) return false;
    setActiveProjectIdState(pid);
    setActiveProjectId(pid);  // immediate, before any dependent refetch
    persist(pid);
    return true;
  }, [persist, projectSwitchBlocked]);

  // Load companies + projects; resolve the active project (stored → else Default).
  // Retries a few times on failure so a transient hiccup on first load (e.g. the auth
  // token still settling) doesn't leave the lists permanently empty.
  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    let lastErr = null;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const [cRes, pRes] = await Promise.all([listCompanies(), listProjects("active")]);
        const comps = cRes.companies || [];
        const projs = pRes.projects || [];
        setCompanies(comps);
        setProjects(projs);

        // Resolve active selection: stored id if still active, else Default, else first.
        let stored = null;
        try { stored = localStorage.getItem(storageKey(userKey)); } catch (e) { /* ignore */ }
        const ids = new Set(projs.map((p) => p.projectId));
        let next = DEFAULT_PROJECT_ID;
        if (stored && ids.has(stored)) next = stored;
        else if (ids.has(DEFAULT_PROJECT_ID)) next = DEFAULT_PROJECT_ID;
        else if (projs.length) next = projs[0].projectId;
        setActiveProjectIdState(next);
        setActiveProjectId(next);
        setLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        await new Promise((r) => setTimeout(r, 700 * (attempt + 1))); // backoff, then retry
      }
    }
    setError(lastErr);
    setLoading(false);
  }, [enabled, userKey]);

  useEffect(() => {
    if (!enabled || didInit.current) return;
    didInit.current = true;
    refresh();
  }, [enabled, refresh]);

  // ── Mutations (refresh + keep a sensible active selection) ──
  const newProject = useCallback(async (displayName, companyName) => {
    const created = await apiCreateProject(displayName, companyName);
    await refresh();
    // Do NOT auto-switch — dropping the user into a brand-new empty project felt
    // "broken". They stay put; the new project appears in the list to switch to when ready.
    return created;
  }, [refresh]);

  const newCompany = useCallback(async (displayName) => {
    const created = await apiCreateCompany(displayName);
    await refresh();
    return created;
  }, [refresh]);

  const renameProjectById = useCallback(async (projectId, displayName) => {
    await apiRenameProject(projectId, { displayName });
    // Optimistic: reflect the new label immediately, then reconcile with the server.
    setProjects((ps) => ps.map((p) => (p.projectId === projectId ? { ...p, displayName } : p)));
    await refresh();
  }, [refresh]);

  const moveProjectToCompany = useCallback(async (projectId, companyId) => {
    await apiRenameProject(projectId, { companyId });
    setProjects((ps) => ps.map((p) => (p.projectId === projectId ? { ...p, companyId } : p)));
    await refresh();
  }, [refresh]);

  const renameCompanyById = useCallback(async (companyId, displayName) => {
    await apiRenameCompany(companyId, displayName);
    // Optimistic: update the company label in place so the sidebar/breadcrumb/bar
    // reflect it instantly (the immediate refetch is now consistent-read too).
    setCompanies((cs) => cs.map((c) => (c.companyId === companyId ? { ...c, displayName } : c)));
    await refresh();
  }, [refresh]);

  const archive = useCallback(async (projectId) => {
    await apiArchiveProject(projectId);
    // force: the project is GONE from the active list — staying on it is not an option, so this
    // switch must happen even while an upload batch is in flight.
    if (projectId === activeProjectId) selectProject(DEFAULT_PROJECT_ID, { force: true });
    await refresh();
  }, [refresh, activeProjectId, selectProject]);

  const reactivate = useCallback(async (projectId) => {
    await apiReactivateProject(projectId);
    await refresh();
  }, [refresh]);

  const remove = useCallback(async (projectId) => {
    await apiDeleteProject(projectId);
    // force: same as archive — the project no longer exists, so the switch can't be refused.
    if (projectId === activeProjectId) selectProject(DEFAULT_PROJECT_ID, { force: true });
    await refresh();
  }, [refresh, activeProjectId, selectProject]);

  const value = {
    companies, projects, loading, error,
    activeProjectId, activeProject, activeCompanyId, activeCompany,
    selectProject, refresh, projectSwitchBlocked,
    newProject, newCompany, renameProjectById, moveProjectToCompany,
    renameCompanyById, archive, reactivate, remove,
    DEFAULT_PROJECT_ID, DEFAULT_COMPANY_ID,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}
